import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiGet, apiPost } from 'src/lib/api/axios';

export type InvitationVerificationStatus = 'valid' | 'expired' | 'already_joined' | 'inactive';

export interface InvitationVerification {
  status: InvitationVerificationStatus;
  first_name?: string | null;
  last_name?: string | null;
  job_title?: string | null;
  email?: string | null;
  organisations?: string[];
  expires_at?: string | null;
  session_token?: string | null;
  token?: string;
}

interface ResendInvitationPayload {
  token: string;
}

interface AuthState {
  loading: boolean;
  error: string | null;
  message: string | null;
  resendInvitation: {
    loading: boolean;
    error: string | null;
    success: boolean;
    requestToken: string | null;
  };
  verifyInvitation: {
    loading: boolean;
    error: string | null;
    retryable: boolean;
    userDetails: InvitationVerification | null;
    token: string | null;
    showResendForm: boolean;
  };
}

const initialState: AuthState = {
  loading: false,
  error: null,
  message: null,
  resendInvitation: {
    loading: false,
    error: null,
    success: false,
    requestToken: null
  },
  verifyInvitation: {
    loading: false,
    error: null,
    retryable: false,
    userDetails: null,
    token: null,
    showResendForm: false
  }
};

// Async thunk for completing profile
export const completeProfile = createAsyncThunk(
  'auth/completeProfile',
  async ({ values, sessionToken }: { values: any; sessionToken: string }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append('first_name', values.firstName);
      formData.append('last_name', values.lastName);
      formData.append('job_title', values.job_title);
      formData.append('new_password', values.password);
      formData.append('marketing_opt_in', String(Boolean(values.marketingOptIn)));
      if (values.organisation) {
        formData.append('organisation', values.organisation);
      }

      const response = await apiPost('auth/complete_profile', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          authorization: `Bearer ${sessionToken}`
        },
        meta: {
          globalError: false,
          context: 'profile-completion'
        }
      });
      return response.data;
    } catch (error: any) {
      // Error is displayed in the form
      return rejectWithValue(error.parsed?.description || 'Failed to complete profile');
    }
  }
);

// Async thunk for completing forum profile
export const signUpForumProfile = createAsyncThunk(
  'auth/signUpForumProfile',
  async ({ values }: { values: any }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append('first_name', values.firstName);
      formData.append('last_name', values.lastName);
      formData.append('job_title', values.job_title);
      formData.append('new_password', values.password);
      formData.append('organisation', values.organisation);
      formData.append('email', values.email);
      formData.append('marketing_opt_in', String(Boolean(values.marketingOptIn)));

      const response = await apiPost('auth/create_forum_user', formData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Async thunk for freemium self-service signup
export const signUpFreemium = createAsyncThunk(
  'auth/signUpFreemium',
  async ({ values }: { values: any }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append('first_name', values.firstName);
      formData.append('last_name', values.lastName);
      formData.append('new_password', values.password);
      formData.append('email', values.email);
      formData.append('marketing_opt_in', String(Boolean(values.marketingOptIn)));
      if (values.jobTitle) {
        formData.append('job_title', values.jobTitle);
      }
      if (values.organisation) {
        formData.append('organisation', values.organisation);
      }

      const response = await apiPost('auth/create_freemium_user', formData, {
        meta: {
          globalError: false,
          context: 'signup'
        }
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.parsed?.description || error.response?.data?.detail || 'Failed to create account');
    }
  }
);

// Async thunk for resending invitation
export const resendInvitation = createAsyncThunk(
  'auth/resendInvitation',
  async (payload: ResendInvitationPayload, { rejectWithValue }) => {
    try {
      const response = await apiPost('/auth/resend_invitation', payload, {
        meta: {
          globalError: false, // Handle locally
          context: 'invitation-resend'
        }
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.parsed?.description || 'Failed to resend invitation');
    }
  }
);

// Async thunk for verifying invitation
export const verifyInvitation = createAsyncThunk(
  'auth/verifyInvitation',
  async (token: string, { rejectWithValue }) => {
    try {
      const response = await apiGet(`/auth/verify_invitation?token=${token}`, {
        meta: {
          globalError: false,
          context: 'invitation-verification'
        }
      });
      return { ...response.data, token } as InvitationVerification;
    } catch (error: any) {
      const retryable = Boolean(error.parsed?.retryable);
      const errorMessage = retryable
        ? error.parsed?.description || 'Unable to verify this invitation. Please try again.'
        : error.parsed?.description ||
          'This invitation is no longer active. Contact your organisation administrator or support@complimind.co.uk.';
      return rejectWithValue({ message: errorMessage, retryable });
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearAuthState: (state) => {
      state.loading = false;
      state.error = null;
      state.message = null;
      state.resendInvitation = {
        loading: false,
        error: null,
        success: false,
        requestToken: null
      };
    },
    clearCompleteProfileState: (state) => {
      state.loading = false;
      state.error = null;
      state.message = null;
    },
    setResendInvitationError: (state, action) => {
      state.resendInvitation.error = action.payload;
    },
    clearResendInvitation: (state) => {
      state.resendInvitation = {
        loading: false,
        error: null,
        success: false,
        requestToken: null
      };
    }
  },
  extraReducers: (builder) => {
    builder
      // Complete Profile cases
      .addCase(completeProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.message = null;
      })
      .addCase(completeProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.message = action.payload.message;
        state.error = action.payload.error || null;
      })
      .addCase(completeProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || 'Failed to complete profile';
      })
      // Resend Invitation cases
      .addCase(resendInvitation.pending, (state, action) => {
        state.resendInvitation.loading = true;
        state.resendInvitation.error = null;
        state.resendInvitation.success = false;
        state.resendInvitation.requestToken = action.meta.arg.token;
      })
      .addCase(resendInvitation.fulfilled, (state, action) => {
        if (action.meta.arg.token !== state.resendInvitation.requestToken) {
          return;
        }
        state.resendInvitation.loading = false;
        state.resendInvitation.success = true;
        state.resendInvitation.error = null;
      })
      .addCase(resendInvitation.rejected, (state, action) => {
        if (action.meta.arg.token !== state.resendInvitation.requestToken) {
          return;
        }
        state.resendInvitation.loading = false;
        state.resendInvitation.success = false;
        state.resendInvitation.error =
          (action.payload as any)?.detail ||
          (action.payload as any)?.message ||
          'Failed to resend invitation. Please contact support.';
      })
      // Verify Invitation cases
      .addCase(verifyInvitation.pending, (state) => {
        state.verifyInvitation.loading = true;
        state.verifyInvitation.error = null;
        state.verifyInvitation.retryable = false;
        state.verifyInvitation.showResendForm = false;
        state.verifyInvitation.userDetails = null;
      })
      .addCase(verifyInvitation.fulfilled, (state, action) => {
        state.verifyInvitation.loading = false;
        state.verifyInvitation.userDetails = action.payload;
        state.verifyInvitation.token = action.payload.token ?? null;
        state.verifyInvitation.error = null;
        state.verifyInvitation.retryable = false;
        state.verifyInvitation.showResendForm = false;
      })
      .addCase(verifyInvitation.rejected, (state, action) => {
        const payload = action.payload as { message?: string; retryable?: boolean } | undefined;
        state.verifyInvitation.loading = false;
        state.verifyInvitation.userDetails = null;
        state.verifyInvitation.retryable = Boolean(payload?.retryable);
        state.verifyInvitation.error =
          payload?.message ||
          (payload?.retryable
            ? 'Unable to verify this invitation. Please try again.'
            : 'This invitation is no longer active. Contact your organisation administrator or support@complimind.co.uk.');
        state.verifyInvitation.showResendForm = false;
      });
  }
});

export const { clearAuthState, clearCompleteProfileState, setResendInvitationError, clearResendInvitation } =
  authSlice.actions;
export default authSlice.reducer;
